import { useEffect, useRef, useState } from "react";
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";

function nationalDisplay(number: NonNullable<ReturnType<typeof parsePhoneNumberFromString>>) {
  return number.formatInternational().replace(`+${number.countryCallingCode} `, "");
}
const countryFlag = (code: string) => String.fromCodePoint(...[...code].map(letter => 127397 + letter.charCodeAt(0)));

export function PhoneInput({id, value, onChange, locale, invalid, required}: {
  id: string; value: string; onChange: (value: string) => void; locale: string; invalid?: boolean; required?: boolean;
}) {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | "">("");
  const parsed = value.startsWith("+") ? parsePhoneNumberFromString(value) : undefined;
  const country = selectedCountry || parsed?.country || "";
  const names = new Intl.DisplayNames([locale], {type: "region"});
  const [national, setNational] = useState(() => parsed ? nationalDisplay(parsed) : value);
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (lastEmitted.current !== value) {
      const imported = value.startsWith("+") ? parsePhoneNumberFromString(value) : undefined;
      setNational(imported ? nationalDisplay(imported) : value);
      setSelectedCountry(imported?.country ?? "");
      lastEmitted.current = value;
    }
  }, [value]);
  function emit(next: string) { lastEmitted.current = next; onChange(next); }
  function changeNumber(text: string, nextCountry: CountryCode | "" = country) {
    setNational(text);
    if (!text.trim()) { emit(""); return; }
    if (text.startsWith("+")) { setSelectedCountry(""); emit(text); return; }
    if (!nextCountry) { emit(text); return; }
    const formatter = new AsYouType(nextCountry);
    formatter.input(text);
    emit(formatter.getNumberValue() ?? text);
  }
  return <div className="phone-input">
    <label className="phone-country"><span className="sr-only">{locale === "es" ? "Código de país" : "Country code"}</span>
      <span className="phone-country-display" aria-hidden="true">{country ? `${countryFlag(country)} +${getCountryCallingCode(country)}` : (locale === "es" ? "Elegir" : "Select")}</span>
      <select aria-label={locale === "es" ? "Código de país" : "Country code"} autoComplete="tel-country-code" value={country} onChange={event => {
        const next = event.target.value as CountryCode | "";
        setSelectedCountry(next);
        if (value) changeNumber(parsed ? String(parsed.nationalNumber) : value, next);
      }}>
        <option value="">{locale === "es" ? "Seleccionar" : "Select"}</option>
        {getCountries().map(code => ({code, name: names.of(code) ?? code})).sort((a,b) => a.name.localeCompare(b.name, locale)).map(({code,name}) => <option key={code} value={code} aria-label={`${name} +${getCountryCallingCode(code)}`}>{countryFlag(code)} +{getCountryCallingCode(code)} {name}</option>)}
      </select>
    </label>
    <label><span className="sr-only">{locale === "es" ? "Número" : "Number"}</span><input id={id} type="tel" inputMode="tel" autoComplete="tel-national" value={national} aria-invalid={invalid} aria-required={required} required={required} onBlur={() => { const number = parsePhoneNumberFromString(value, country || undefined); if (number) setNational(nationalDisplay(number)); }} onChange={event => changeNumber(event.target.value)} /></label>
  </div>;
}
